import { SortInput } from '@apeiron/common'
import {
  StarEmporiumSortItems,
  StarFilterInput,
  StarListMode,
  StarLite,
  StarNormalSortItems,
  StarSortBy
} from '@apeiron/star'
import {
  ApeironTitle,
  DEFAULT_LIST_SIZE,
  noForwardCustomProps,
  PageInfo,
  Pagination
} from '@apeiron/temp'
import { usePath } from '@apeiron/ui'
import { Box, styled, Typography } from '@mui/material'
import ListTemplate from '@src/components/layout/ListTemplate'
import SortDropdown from '@src/components/share/generic/SortDropdown'
import StarFilter from '@src/components/stars/list/StarFilter'
import StarList from '@src/components/stars/list/StarList'
import { GAAssetType } from '@src/constants/googleAnalytics'
import { WhiteList } from '@src/constants/page'
import { ROUTE } from '@src/constants/route'
import useStarFilterQuery from '@src/hooks/apeiron/star/useStarFilterQuery'
import useQueryPaging from '@src/hooks/common/router/useQueryPaging'
import useGetMyStars from '@src/hooks/graphql/useGetMyStars'
import useGetStars from '@src/hooks/graphql/useGetStars'
import queryString from 'query-string'
import * as R from 'ramda'
import { FC, ReactNode, useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import useAsyncEffect from 'use-async-effect'

const StyledListTemplate = styled(ListTemplate)`
  .Custom-Layout-Container {
    ${props => props.theme.breakpoints.up('res1024')} {
      grid-template-columns: 355px 1fr;
    }
  }
`

const Filter = styled(
  StarFilter,
  noForwardCustomProps(['popupMode'])
)<StarFilterProps>`
  ${props => props.theme.breakpoints.up('res1024')} {
    ${props =>
      props.popupMode
        ? `
          height: 100%;
          position: sticky;
          top: 0px;
        `
        : `
          height: calc(100vh - 116px);
          top: 116px;
        `}
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    width: 100%;
  }
  overflow-y: auto;
`

const Total = styled(Typography)`
  font-size: 24px;
  line-height: 28px;
  color: white;
  ${props => props.theme.breakpoints.down('res1024')} {
    padding: 8px 20px;
  }
`

const ListAndPageContainer = styled(Box)`
  position: relative;
  width: 100%;
  flex: 1;
  padding-top: 10px;
`

const ListContainer = styled(Box)`
  position: relative;
  min-height: 270px;
`

const PageContainer = styled(Box)`
  margin-top: 25px;
  width: 100%;
  height: 50px;
`

const Dropdown = styled(SortDropdown<StarSortBy>)`
  height: 100%;
  margin-right: 12px;
`

const HeaderContainer = styled(Box)`
  flex-direction: column;
`

const StyledApeironTitle = styled(ApeironTitle)`
  margin-bottom: 10px;
`

const StarListContent: FC<StarListContentProps> = (
  props: StarListContentProps
) => {
  const {
    data,
    emptyMessage,
    emptyRecommendation,
    enableLink,
    loading,
    pageInfo,
    showExplain,
    handleOnNextPage,
    handleOnPreviousPage,
    handleOnRenderURL,
    handleOnSubmitPage,
    onItemClick
  } = props

  return (
    <ListAndPageContainer>
      <ListContainer>
        <StarList
          emptyDescription={emptyMessage || ''}
          emptyRecommendation={emptyRecommendation}
          enableLink={enableLink}
          loading={loading}
          showExplain={showExplain}
          stars={data}
          onRenderURL={handleOnRenderURL}
          onItemClick={onItemClick}
        />
      </ListContainer>
      {pageInfo.totalPage > 0 && (
        <PageContainer>
          <Pagination
            prevDisabled={pageInfo.page === 1}
            nextDisabled={R.isNil(pageInfo.nextCursor)}
            onPrev={handleOnPreviousPage}
            onNext={handleOnNextPage}
            onSubmit={handleOnSubmitPage}
            value={pageInfo.page}
            totalPage={pageInfo.totalPage}
          />
        </PageContainer>
      )}
    </ListAndPageContainer>
  )
}

const StarListWithFilter: FC<Props> = (props: Props) => {
  const { t } = useTranslation()

  const { replacePath } = usePath(WhiteList)

  const {
    allowFetch = true,
    allowFavorite = true,
    className,
    emptyMessage = t('star.list.empty'),
    enableLink = true,
    mode = StarListMode.EMPORIUM,
    pageSize = DEFAULT_LIST_SIZE,
    thirdNavBar,
    title,
    postProcessData,
    onItemClick,
    onFetchSuccess,
    onAfterFilterChange
  } = props

  const {
    loading: starsLoading,
    fetch: fetchStars,
    data: stars,
    pageInfo: starsPageInfo
  } = useGetStars()

  const {
    loading: myStarsLoading,
    fetch: fetchMyStars,
    data: myStars,
    pageInfo: myStarsPageInfo
  } = useGetMyStars()

  const publicMode =
    mode === StarListMode.EMPORIUM || mode === StarListMode.OTHERS_INVENTORY

  const data = useMemo(() => {
    const result = publicMode ? stars : myStars

    return postProcessData ? postProcessData(result) : result
  }, [myStars, postProcessData, publicMode, stars])

  const loading = publicMode ? starsLoading : myStarsLoading

  const fetch = publicMode ? fetchStars : fetchMyStars

  const pageInfo = publicMode ? starsPageInfo : myStarsPageInfo

  const popupMode = mode === StarListMode.POPUP_SELECT_INVENTORY

  const queryParams = { supportUrl: !popupMode }

  const { queryPageNumber, setPageNumberQuery } = useQueryPaging(queryParams)

  const { filterInput, sortInput, setBothInput, setFilterInput, setSortInput } =
    useStarFilterQuery(queryParams)

  const filterHasChangeRef = useRef(false)

  const doFetch = useCallback(
    async (parameters: any) => {
      let extraParams = {}

      switch (mode) {
        case StarListMode.FAVORITE:
          extraParams = { bookmarked: true }

          break

        case StarListMode.INVENTORY:
        case StarListMode.OTHERS_INVENTORY:
        case StarListMode.POPUP_SELECT_INVENTORY:
          extraParams = { bookmarked: false }

          break
      }

      const requestInput = { ...parameters, ...extraParams }

      const result = await fetch(requestInput)

      if (onFetchSuccess) {
        onFetchSuccess(
          result.data,
          filterHasChangeRef.current,
          requestInput.filterInput
        )
      }

      filterHasChangeRef.current = false
    },
    [fetch, mode, onFetchSuccess]
  )

  const handleOnFilterChange = useCallback(
    (filter: StarFilterInput, sort?: SortInput<StarSortBy>) => {
      if (filter && sort) {
        // filter by favorite
        setBothInput(filter, sort)
      } else {
        setFilterInput(filter)

        if (onAfterFilterChange) {
          onAfterFilterChange(GAAssetType.Star, filterInput, filter)
        }
      }

      filterHasChangeRef.current = true
    },
    [setBothInput, setFilterInput, filterInput, onAfterFilterChange]
  )

  const handleOnPreviousPage = useCallback(async () => {
    setPageNumberQuery(pageInfo.page - 1)
    doFetch({
      pageInput: {
        page: pageInfo.page - 1
      },
      filterInput,
      sortInput
    })
  }, [doFetch, filterInput, pageInfo.page, setPageNumberQuery, sortInput])

  const handleOnNextPage = useCallback(async () => {
    setPageNumberQuery(pageInfo.page + 1)
    doFetch({
      pageInput: {
        page: pageInfo.page + 1
      },
      filterInput,
      sortInput
    })
  }, [doFetch, filterInput, pageInfo.page, setPageNumberQuery, sortInput])

  const handleOnSubmitPage = useCallback(
    async (page: number) => {
      setPageNumberQuery(page)
      doFetch({
        pageInput: {
          page
        },
        filterInput,
        sortInput
      })
    },
    [doFetch, filterInput, setPageNumberQuery, sortInput]
  )

  const handleOnSortChange = useCallback(
    (input: SortInput<StarSortBy>) => {
      setSortInput(input)
    },
    [setSortInput]
  )

  const handleOnRenderURL = useCallback(
    (star: StarLite) => {
      const url = queryString.stringifyUrl({
        url: ROUTE.MARKETPLACE_STAR_DETAIL,
        query: {
          starId: star.id
        }
      })

      return replacePath(url)
    },
    [replacePath]
  )

  useAsyncEffect(async () => {
    if (allowFetch) {
      await doFetch({
        pageInput: {
          page: queryPageNumber
        },
        filterInput,
        sortInput
      })
    }
  }, [allowFetch, pageSize, filterInput, sortInput, queryPageNumber])

  return (
    <StyledListTemplate
      className={className}
      popupMode={popupMode}
      thirdNavBar={thirdNavBar}
      headerLeft={
        <HeaderContainer>
          {title && <StyledApeironTitle text={title} />}
          <Total>{t('star.list.total', { count: pageInfo.total })}</Total>
        </HeaderContainer>
      }
      filter={
        <Filter
          allowFavorite={allowFavorite}
          filterInput={filterInput}
          popupMode={popupMode}
          sortInput={sortInput}
          onFilterChange={handleOnFilterChange}
        />
      }
      sortDropDown={
        <Dropdown
          items={
            mode === StarListMode.EMPORIUM
              ? StarEmporiumSortItems
              : StarNormalSortItems
          }
          value={sortInput}
          onSelectChange={handleOnSortChange}
        />
      }
      mainContent={
        <StarListContent
          data={data}
          emptyMessage={emptyMessage}
          emptyRecommendation={mode === StarListMode.INVENTORY}
          enableLink={enableLink}
          loading={loading}
          pageInfo={pageInfo}
          handleOnNextPage={handleOnNextPage}
          handleOnPreviousPage={handleOnPreviousPage}
          handleOnRenderURL={handleOnRenderURL}
          handleOnSubmitPage={handleOnSubmitPage}
          onItemClick={onItemClick}
        />
      }
    />
  )
}

type StarFilterProps = {
  popupMode?: boolean
}

type PopupProps = {
  allowFavorite?: boolean
  postProcessData?: (stars: StarLite[]) => StarLite[]
}

type StarListContentProps = {
  data: StarLite[]
  emptyMessage?: string
  emptyRecommendation?: boolean
  enableLink: boolean
  loading: boolean
  pageInfo: PageInfo
  showExplain?: boolean
  handleOnNextPage: () => Promise<void>
  handleOnPreviousPage: () => Promise<void>
  handleOnRenderURL: (star: StarLite) => string
  handleOnSubmitPage: (page: number) => Promise<void>
  onItemClick?: (star: StarLite) => void
}

type Props = {
  allowFetch?: boolean
  className?: string
  emptyMessage?: string
  enableLink?: boolean
  mode?: StarListMode
  pageSize?: number
  thirdNavBar?: ReactNode
  title?: string
  onFetchSuccess?: (
    stars: StarLite[],
    filterHasChange: boolean,
    filterInput?: StarFilterInput
  ) => void
  onItemClick?: (star: StarLite) => void
  onAfterFilterChange?: (
    type: GAAssetType,
    originalFilter: StarFilterInput,
    newFilter: StarFilterInput
  ) => void
} & PopupProps

export type { Props as StarListWithFilterProps }

export default StarListWithFilter
